<template>
	<header id="header" ref="header" :login="login" data-qa="header-layout">
		<div id="header--content">
			<router-link
				to="/"
				class="header__brand header__brand--primary"
				data-qa="header-home-link"
				:title="$t('a11y.homePage')"
				:aria-label="$t('a11y.homePage')"
			>
				<span class="header__brand-logo header__brand-logo--lg" @click="onClickCard('logoLg_tab_click', '/')">
					<LogoLg />
				</span>
				<span class="header__brand-divider" aria-hidden="true"></span>
				<span class="header__brand-logo header__brand-logo--club" @click="onClickCard('logoClub_tab_click', '/')">
					<LogoClub logoSymbol="var(--white)" />
				</span>
			</router-link>

			<section
				id="header-navigation"
				class="header__navigation-layer"
				:class="{ 'is-open': isNavMobile }"
				data-qa="header-mobile-menu"
				:aria-hidden="!isDesktop && !isNavMobile ? 'true' : null"
			>
				<Nav data-qa="header-nav-menu" :on-click-card="onClickCard" @navigate="closeMenuMobile" />

				<router-link
					to="/"
					class="header__club-link"
					data-qa="header-club-home-link"
					:title="$t('a11y.homePage')"
					:aria-label="$t('a11y.homePage')"
					@click="onClickCard('logoClub_tab_click', '/')"
				>
					<LogoClub logoSymbol="var(--white)" />
				</router-link>

				<router-link
					ref="searchLink"
					:to="searchPath"
					class="header__search"
					data-qa="header-search-link"
					:title="$t('a11y.searchSite')"
					:aria-label="$t('a11y.searchSite')"
					@click="onClickCard('buscar_tab_click', 'buscar')"
				>
					<Icons name="search" />
					<span>{{ $t('a11y.searchSite') }}</span>
				</router-link>

				<div v-if="!login.logged" class="header__drawer-auth">
					<router-link
						data-qa="header-mobile-signup-link"
						class="header__drawer-signup"
						:to="signupPath"
						:aria-label="$t('a11y.signUpPlatform')"
						@click="closeMenuMobile()"
					>
						{{ $t('header.signUp') }}
					</router-link>
					<router-link
						:to="signinPath"
						class="header__drawer-signin"
						data-qa="header-mobile-signin-link"
						:title="$t('a11y.signInPlatform')"
						:aria-label="$t('a11y.signInPlatform')"
						@click="closeMenuMobile()"
					>
						{{ $t('signUp.alreadyMember') }}
					</router-link>
				</div>
			</section>

			<div class="header__actions">
				<router-link
					v-if="!login.logged"
					:to="signinPath"
					class="header__signin"
					data-qa="header-signin-link"
					:title="$t('a11y.signInPlatform')"
					:aria-label="$t('a11y.signInPlatform')"
				>
					{{ $t('buttons.entrar') }}
				</router-link>

				<router-link
					v-if="!login.logged"
					data-qa="header-signup-link"
					class="header__signup"
					:to="signupPath"
					:aria-label="$t('a11y.signUpPlatform')"
				>
					{{ $t('header.signUp') }}
				</router-link>

				<button
					v-if="login.logged"
					ref="profileButton"
					type="button"
					class="header__profile"
					data-qa="header-user-menu-button"
					:aria-expanded="isNavUser ? 'true' : 'false'"
					aria-controls="header-user-menu"
					:title="$t('a11y.openLoggedUserMenu')"
					:aria-label="$t('a11y.openLoggedUserMenu')"
					@click="toggleMenuUser"
				>
					<span data-qa="header-user-initials-text">{{ getNameIcon(login.name) }}</span>
				</button>

				<button
					ref="menuButton"
					type="button"
					class="header__menu-button"
					:class="{ 'is-active': isNavMobile }"
					data-qa="header-mobile-menu-button"
					:aria-expanded="isNavMobile ? 'true' : 'false'"
					aria-controls="header-navigation"
					:title="$t(isNavMobile ? 'a11y.closeMobileMenu' : 'a11y.openMobileMenu')"
					:aria-label="$t(isNavMobile ? 'a11y.closeMobileMenu' : 'a11y.openMobileMenu')"
					@click="toggleMenuMobile"
				>
					<span aria-hidden="true"></span>
					<span aria-hidden="true"></span>
				</button>

				<Transition name="header-menu">
					<div id="header-user-menu" v-if="isNavUser" class="header__user-menu" data-qa="header-user-menu">
						<router-link
							:to="{ name: 'minha-area' }"
							data-qa="header-my-area-link"
							:title="$t('a11y.goToMyArea')"
							:aria-label="$t('a11y.goToMyArea')"
						>
							<Icons name="user" />
							{{ $t('header.menuArea') }}
						</router-link>
						<router-link
							to="/"
							data-qa="header-signout-link"
							:title="$t('a11y.signOutPlatform')"
							:aria-label="$t('a11y.signOutPlatform')"
							@click="logout"
						>
							<Icons name="logout" />
							{{ $t('header.menuSair') }}
						</router-link>
					</div>
				</Transition>
			</div>
		</div>
	</header>
	<AnnouncementModal v-if="isComunidadesRoute" data-qa="header-announcement-modal" />
</template>

<script>
import Icons from '@/components/Icons.vue';
import LogoLg from '@/components/template/LogoLg.vue';
import LogoClub from '@/components/template/LogoClub.vue';
import Nav from '@/components/template/Nav.vue';
import AnnouncementModal from '@/components/AnnouncementModal.vue';

export default {
	components: { LogoLg, LogoClub, Nav, Icons, AnnouncementModal },
	data() {
		return {
			isNavUser: false,
			isNavMobile: false,
			isDesktop: false,
			viewportMedia: null,
			bodyOverflow: null
		};
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside);
		document.addEventListener('keydown', this.handleKeydown);
		this.viewportMedia = window.matchMedia('(min-width: 75em)');
		this.isDesktop = this.viewportMedia.matches;
		if (this.viewportMedia.addEventListener)
			this.viewportMedia.addEventListener('change', this.handleViewportChange);
		else
			this.viewportMedia.addListener(this.handleViewportChange);
	},
	beforeUnmount() {
		document.removeEventListener('click', this.handleClickOutside);
		document.removeEventListener('keydown', this.handleKeydown);
		if (this.viewportMedia?.removeEventListener)
			this.viewportMedia.removeEventListener('change', this.handleViewportChange);
		else
			this.viewportMedia?.removeListener(this.handleViewportChange);
		this.unlockBodyScroll();
	},
	computed: {
		signinRoute() {
			const query = { ...this.$route.query };
			if (!query.redirect)
				query.redirect = this.$route.fullPath;
			return { name: 'signin', query };
		},
		signinPath() {
			return this.$router.resolve(this.signinRoute).fullPath;
		},
		signupPath() {
			return this.$router.resolve({ name: 'signup' }).path;
		},
		searchPath() {
			return this.$router.resolve({ name: 'buscar' }).path;
		},
		isComunidadesRoute() {
			return (this.$route?.path || '').startsWith('/forum');
		},
		login() {
			return {
				name: this.$store.state.auth.user?.name,
				logged: !!this.$store.state.auth.user
			};
		}
	},
	watch: {
		'$route.fullPath'() {
			this.closeMenuMobile();
			this.isNavUser = false;
		}
	},
	methods: {
		onClickCard(element, content, index, to) {
			const destination = to || content || this.$route.fullPath;
			const href = this.$router.resolve(destination).href;
			const fullUrl = new URL(href, window.location.origin).toString();

			this.$store.dispatch('route/gaEvent', {
				element,
				content,
				index,
				to: fullUrl
			});
		},
		logout() {
			this.$store.dispatch('auth/logout').then(() => {
				this.$router.push({ name: 'signin' });
			});
		},
		getNameIcon(name) {
			const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
			if (!parts.length) return '?';
			return `${parts[0][0]}${parts.length > 1 ? parts[1][0] : ''}`.toUpperCase();
		},
		handleClickOutside(event) {
			const header = this.$refs.header;
			if (header && !header.contains(event.target)) {
				this.closeMenuMobile();
				this.isNavUser = false;
			}
		},
		handleKeydown(event) {
			if (event.key !== 'Escape') return;
			if (this.isNavMobile) {
				this.closeMenuMobile(true);
				return;
			}
			if (this.isNavUser) {
				this.isNavUser = false;
				this.$nextTick(() => this.$refs.profileButton?.focus());
			}
		},
		handleViewportChange(event) {
			this.isDesktop = event.matches;
			if (this.isDesktop)
				this.closeMenuMobile();
		},
		toggleMenuUser() {
			this.closeMenuMobile();
			this.isNavUser = !this.isNavUser;
			this.onClickCard('menu_usuario_tab_click');
		},
		toggleMenuMobile() {
			if (this.isNavMobile) {
				this.closeMenuMobile();
				return;
			}
			this.isNavUser = false;
			this.isNavMobile = true;
			this.lockBodyScroll();
			this.$nextTick(() => {
				const searchLink = this.$refs.searchLink?.$el || this.$refs.searchLink;
				searchLink?.focus();
			});
		},
		closeMenuMobile(returnFocus = false) {
			if (!this.isNavMobile) return;
			this.isNavMobile = false;
			this.unlockBodyScroll();
			if (returnFocus)
				this.$nextTick(() => this.$refs.menuButton?.focus());
		},
		lockBodyScroll() {
			if (this.bodyOverflow !== null) return;
			this.bodyOverflow = document.body.style.overflow;
			document.body.style.overflow = 'hidden';
		},
		unlockBodyScroll() {
			if (this.bodyOverflow === null) return;
			document.body.style.overflow = this.bodyOverflow;
			this.bodyOverflow = null;
		}
	}
};
</script>

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_colors' as *
@use '../../assets/sass/utilities/_responsive' as *

#header
	--header-height: 3.875rem
	position: relative
	z-index: 110
	width: 100%
	background-color: var(--lg-red-active)

	&--content
		display: grid
		grid-template-columns: minmax(0, 1fr) auto auto
		align-items: center
		gap: 0.5rem
		width: min(100%, var(--contentSize))
		height: var(--header-height)
		margin-inline: auto
		padding-inline: var(--mobile-p)

.header__brand
	display: flex
	align-items: center
	gap: clamp(0.375rem, 1.5vw, 0.625rem)
	min-width: 0
	width: fit-content
	max-width: 100%
	color: var(--white)
	text-decoration: none

	&:focus-visible
		outline: 0.125rem solid var(--white)
		outline-offset: 0.25rem

.header__brand-logo
	display: block
	flex: 0 1 auto
	line-height: 0

	&--lg
		display: none

	&--club
		width: clamp(9rem, 42vw, 10.5rem)

		:deep(svg)
			display: block

.header__brand-divider
	display: none

.header__club-link
	display: none
	line-height: 0

	:focus-visible
		outline: 0.125rem solid var(--white)
		outline-offset: 0.25rem

	:deep(svg)
		display: block

.header__navigation-layer
	position: fixed
	top: var(--header-height)
	right: 0
	bottom: auto
	left: 0
	display: flex
	flex-direction: column
	gap: 1rem
	height: calc(100vh - var(--header-height))
	height: calc(100dvh - var(--header-height))
	max-height: calc(100dvh - var(--header-height))
	padding: 1.25rem var(--mobile-p) max(2rem, calc(1rem + env(safe-area-inset-bottom)))
	background-color: var(--white)
	overflow-x: hidden
	overflow-y: auto
	overscroll-behavior: contain
	opacity: 0
	visibility: hidden
	translate: 0 -0.5rem
	pointer-events: none
	transition: opacity 0.2s ease, translate 0.2s ease, visibility 0s linear 0.2s

	&.is-open
		opacity: 1
		visibility: visible
		translate: 0 0
		pointer-events: auto
		transition-delay: 0s

.header__search
	order: -1
	display: flex
	align-items: center
	gap: 0.75rem
	min-height: 3rem
	padding-inline: 0.875rem
	border: 0.0625rem solid var(--lg-light-gray-300)
	border-radius: 99999px
	color: var(--lg-red-active)
	font-family: var(--lgei-text)
	font-weight: 600
	text-decoration: none

	&:focus-visible
		outline: 0.125rem solid var(--lg-red-active)
		outline-offset: 0.125rem

	i
		width: 1.25rem

.header__drawer-auth
	display: flex
	flex-direction: column
	gap: 0.875rem
	margin-top: auto
	padding-top: 1.25rem
	border-top: 0.0625rem solid var(--lg-light-gray-300)

.header__drawer-signup,
.header__signup
	display: flex
	align-items: center
	justify-content: center
	min-height: 2.75rem
	padding: 0.75rem 1.25rem
	border: 0.0625rem solid transparent
	border-radius: 99999px
	background-color: var(--lg-red-active)
	color: var(--white)
	font-family: var(--lgei-text)
	font-size: 0.875rem
	font-weight: 600
	line-height: 1
	text-decoration: none
	white-space: nowrap
	transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease

	&:focus-visible
		outline: 0.125rem solid var(--lg-red-active)
		outline-offset: 0.125rem

.header__drawer-signup
	width: 100%

.header__drawer-signin
	align-self: center
	color: var(--lg-red-active)
	font-family: var(--lgei-text)
	font-size: 0.9375rem
	font-weight: 600
	text-align: center

	&:focus-visible
		outline: 0.125rem solid var(--lg-red-active)
		outline-offset: 0.25rem

.header__actions
	display: flex
	align-items: center
	gap: 0.5rem
	position: relative

.header__signin
	color: var(--white)
	font-family: var(--lgei-text)
	font-size: 0.875rem
	font-weight: 600
	text-decoration: none
	white-space: nowrap

	&:focus-visible
		outline: 0.125rem solid var(--white)
		outline-offset: 0.25rem

.header__signup
	display: none

.header__profile
	display: grid
	place-items: center
	width: 2.25rem
	height: 2.25rem
	padding: 0
	border: 0
	border-radius: 50%
	background-color: var(--white)
	color: var(--lg-red-active)
	cursor: pointer
	font-family: var(--lgei-text)
	font-size: 0.75rem
	font-weight: 600
	transition: box-shadow 0.2s ease, scale 0.2s ease

	&:hover
		scale: 1.05

	&:focus-visible
		outline: 0.125rem solid var(--white)
		outline-offset: 0.1875rem

.header__menu-button
	display: flex
	flex-direction: column
	align-items: center
	justify-content: center
	gap: 0.375rem
	width: 2.75rem
	height: 2.75rem
	padding: 0.625rem
	border: 0
	background: transparent
	cursor: pointer

	span
		display: block
		width: 1.5rem
		height: 0.125rem
		border-radius: 99999px
		background-color: var(--white)
		transition: rotate 0.2s ease, translate 0.2s ease

	&.is-active
		span:first-child
			rotate: 45deg
			translate: 0 0.25rem

		span:last-child
			rotate: -45deg
			translate: 0 -0.25rem

	&:focus-visible
		outline: 0.125rem solid var(--white)
		outline-offset: -0.125rem

.header__user-menu
	position: absolute
	top: calc(var(--header-height) - 0.5rem)
	right: 0
	display: flex
	flex-direction: column
	width: min(16rem, calc(100vw - 2rem))
	padding: 0.5rem
	border: 0.0625rem solid var(--lg-light-gray-300)
	border-radius: 0.625rem
	background-color: var(--white)
	box-shadow: rgba(var(--black-rgb), 0.12) 0 0.5rem 1.25rem

	a
		display: flex
		align-items: center
		gap: 0.75rem
		padding: 0.75rem 0.875rem
		border-radius: 0.375rem
		color: var(--lg-red-active)
		font-family: var(--lgei-text)
		font-weight: 600
		text-decoration: none
		transition: background-color 0.2s ease

		:deep(i)
			display: block
			flex: 0 0 1.25rem
			width: 1.25rem

		&:hover,
		&:focus-visible
			background-color: var(--lg-light-gray-100)

		&:focus-visible
			outline: 0.125rem solid var(--lg-red-active)
			outline-offset: -0.125rem

.header-menu-enter-active,
.header-menu-leave-active
	transition: opacity 0.15s ease, translate 0.15s ease

.header-menu-enter-from,
.header-menu-leave-to
	opacity: 0
	translate: 0 -0.375rem

@media (prefers-reduced-motion: reduce)
	.header__navigation-layer,
	.header__menu-button span,
	.header__profile,
	.header__drawer-signup,
	.header__signup,
	.header__user-menu a,
	.header-menu-enter-active,
	.header-menu-leave-active
		transition: none

+respond-to($breakpoint-xs-min)
	#header
		--header-height: 4.25rem

		&--content
			grid-template-columns: auto minmax(0, 1fr) auto
			gap: clamp(0.75rem, 1.5vw, 1.5rem)
			padding-inline: 2rem

	.header__brand
		gap: 0.625rem

	.header__brand-logo
		&--lg
			display: block
			width: 3.75rem

		&--club
			display: none

	.header__club-link
		display: block
		width: clamp(8.5rem, 10vw, 10.5rem)

	.header__navigation-layer
		position: static
		display: grid
		grid-template-columns: minmax(0, 1fr) auto auto
		align-items: center
		gap: clamp(0.5rem, 1vw, 1rem)
		min-width: 0
		height: auto
		max-height: none
		padding: 0
		background: transparent
		overflow: visible
		opacity: 1
		visibility: visible
		translate: none
		pointer-events: auto
		transition: none

	.header__navigation-layer :deep(#nav)
		grid-column: 1
		grid-row: 1
		width: auto
		min-width: 0
		gap: clamp(0.75rem, 1.5vw, 2rem)
		justify-content: center

	.header__club-link
		grid-column: 2
		grid-row: 1

	.header__search
		order: 0
		grid-column: 3
		grid-row: 1
		flex: 0 0 2.75rem
		justify-content: center
		width: 2.75rem
		min-height: 2.75rem
		padding: 0
		border: 0
		color: var(--white)

		span
			display: none

		&:focus-visible
			outline-color: var(--white)

	.header__drawer-auth,
	.header__menu-button
		display: none

	.header__signup
		display: flex
		min-height: 2.25rem
		padding-block: 0.5rem
		border-color: currentColor
		background-color: var(--white)
		color: var(--lg-dark-gray-200)

		&:hover
			border-color: var(--white)
			background-color: transparent
			color: var(--white)

		&:focus-visible
			outline-color: var(--white)

	.header__actions
		gap: 0.75rem

	.header__signin
		font-size: 0.875rem

	.header__user-menu
		top: calc(var(--header-height) - 0.25rem)
</style>
